import $ from 'jquery';
import { $Win } from '../utils/jQueryGlobals';
import Lenis from 'lenis';
import { gsapSingleton, scrollTriggerSingleton } from '../gsap';

const gsap = gsapSingleton();
const ScrollTrigger = scrollTriggerSingleton();

export default class Context {
  /**
   * Il costruttore ora separa la logica in due parti:
   * 1. initOnce(): Funzionalità globali da eseguire una sola volta.
   * 2. initComponentsAndPageLogic(): Funzionalità legate al contenuto, da rieseguire ad ogni cambio pagina.
   */
  constructor(options = {}) {
    this.lenis = null;
    this.lenisEnabled = options.lenisEnabled !== false; // Default true
    this.initOnce();
    this.initComponentsAndPageLogic();
  }

  initOnce() {
    document.documentElement.classList.remove('no-js');
    this.initMouseCoords();
    this.initScrollEvent();

    // --- 2. INIZIALIZZAZIONE E INTEGRAZIONE DI LENIS ---
    if (this.lenisEnabled) {
      this.lenis = new Lenis({
        anchors: true,
      });

      // Esponi Lenis globalmente per altri componenti
      window.lenis = this.lenis;

      // Sincronizza Lenis con GSAP ScrollTrigger.
      // Ad ogni "scroll" virtuale di Lenis, comunica a ScrollTrigger la nuova posizione.
      this.lenis.on('scroll', ScrollTrigger.update);

      // Integra il ciclo di aggiornamento di Lenis nel "ticker" di GSAP (il suo ciclo di rendering).
      // Questo è più efficiente che avere due cicli requestAnimationFrame separati e garantisce
      // che le animazioni siano perfettamente sincronizzate.
      gsap.ticker.add((time) => {
        this.lenis.raf(time * 1000); // il tempo va convertito in millisecondi
      });

      // Disabilita una funzione di GSAP che potrebbe creare ritardi,
      // garantendo una reattività immediata.
      gsap.ticker.lagSmoothing(0);

      console.log('✅ Lenis inizializzato e sincronizzato con GSAP.');
    } else {
      console.log('⚠️ Lenis disabilitato tramite configurazione.');
      window.lenis = null;
    }

		this.mainMenuInstance = null;
		this.languageSwitcherInstance = null;
  }


  initComponentsAndPageLogic() {
    this.loadComponents();

    $(() => {
      $('.rte table').wrap('<div class="responsive-table" />');
    });
  }

  /**
   * Metodo pubblico chiamato da App.js sull'hook 'content:replace' di Swup.
   * Si occupa di preparare e reinizializzare la nuova pagina.
   */
  reinit() {
    this.initComponentsAndPageLogic();

    // Reinizializza il MainMenu per aggiornare gli stili in base al nuovo body class
    if (this.mainMenuInstance && typeof this.mainMenuInstance.reinit === 'function') {
      this.mainMenuInstance.reinit();
    }

    // Reinizializza il LanguageSwitcher per aggiornare la lingua in base all'URL
    if (this.languageSwitcherInstance && typeof this.languageSwitcherInstance.reinit === 'function') {
      this.languageSwitcherInstance.reinit();
    }
  }

  /**
   * Metodo pubblico chiamato da App.js sull'hook 'visit:start' di Swup.
   * Fondamentale per "pulire" le animazioni GSAP (specialmente ScrollTrigger)
   * prima che i loro elementi target vengano rimossi dal DOM, prevenendo memory leak e bug.
   * Lenis NON viene toccato, perché deve rimanere attivo tra le pagine.
   */
  cleanupBeforeTransition() {
		
		// Chiude il menu se è aperto
    if (this.mainMenuInstance && this.mainMenuInstance.isOpen) {
      this.mainMenuInstance.closeMenu(); // 3. Chiama il metodo closeMenu()
    }

    // Trova tutte le istanze di ScrollTrigger e le "uccide" in modo pulito.
    if (window.ScrollTrigger) {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    }

    // Se hai animazioni GSAP globali non legate a ScrollTrigger che potrebbero
    // continuare a girare, puoi terminarle qui. Esempio:
    // gsap.killTweensOf('.elemento-con-animazione-persistente');
  }


  /**
   * Aggiunge un event listener ottimizzato usando requestAnimationFrame
   * per evitare di sovraccaricare il browser.
   */
  addEventListenerFrame(event, callback) {
    let timeout;
    if (typeof event == 'string') {
      event = [event];
    }
    event.forEach((ev) => {
      window.addEventListener(ev, (e) => {
        if (timeout) {
          window.cancelAnimationFrame(timeout);
        }
        timeout = window.requestAnimationFrame(() => callback(e));
      });
    });
  }

  initScrollEvent() {
    this.addEventListenerFrame(['scroll', 'resize'], () => {
      $Win.trigger('efficient-scroll', document.documentElement.scrollTop);
    });
    $Win.trigger('efficient-scroll', document.documentElement.scrollTop);
  }

  initMouseCoords() {
    this.addEventListenerFrame('mousemove', (e) => {
      document.documentElement.style.setProperty('--mouse-client-x', `${e.clientX}px`);
      document.documentElement.style.setProperty('--mouse-client-y', `${e.clientY}px`);
    });
  }

  /**
   * Carica dinamicamente i componenti presenti nel DOM.
   * La funzione è asincrona per attendere il caricamento dei moduli JS.
   */
  async loadComponents(name = false, context) {
    const selector = name
      ? `[data-g-component="${name}"]`
      : '[data-g-component]';

    const elementsToInitialize = $(selector, context).get().filter(elem => !$(elem).data('g-component-initialized'));

    await Promise.all(elementsToInitialize.map(async (elem) => {
      const $elem = $(elem);
      const componentName = $elem.data('g-component');

      if (componentName) {
        try {
          const module = await import(`../components/${componentName}.js`);
          const ComponentClass = module.default;

          if (typeof ComponentClass === 'function') {
            const instance = new ComponentClass(elem, this);
            $elem.data('g-component-initialized', true);

            // 2. Se il componente è MainMenu, salvalo
            if (componentName === 'MainMenu') {
              this.mainMenuInstance = instance;
            }

            // Salva l'istanza di LanguageSwitcher per poterlo reinizializzare con Swup
            if (componentName === 'LanguageSwitcher') {
              this.languageSwitcherInstance = instance;
            }

          } else {
            console.error(`Il componente "${componentName}" è stato caricato ma non è una classe/funzione valida.`);
          }
        } catch (error) {
          console.error(`Errore durante il caricamento del componente "${componentName}":`, error);
        }
      }
    }));
  }

  getCssVar(name, elem = document.documentElement) {
    return getComputedStyle(elem).getPropertyValue(name);
  }

  getBreakpoint(bp, elem = document.documentElement) {
    let value = this.getCssVar(`--breakpoint-${bp}`, elem);
    if (value) {
      value = parseInt(value);
    }
    return value || 0;
  }

}
